/**
 * Totem Metadata Generator
 * Utility for creating standardized NFT metadata for totems
 * Uses OpenRarity mechanics (no custom rarity calculation)
 */

import { COLLECTIONS } from "./api"
import type { CollectionSnapshot } from "./totem-ownership"
import { getBlockVariant } from "./seasonal-images"
import { getDisplayNameFromSlug } from "./opensea-utils"

// Function to get the correct base URL
function getBaseUrl(): string {
  // Always use production domain for metadata/images,
  // so marketplaces (Etherscan/OpenSea) get stable URLs
  return 'https://nftotem.ilyakazakov.com'
}

export interface TotemCollection {
  id: string
  name: string
  image: string
  cubeImage: string
  floorPrice?: number
}

export interface TotemMetadata {
  name: string
  description: string
  image: string
  external_url?: string
  attributes: TotemAttribute[]
  properties?: {
    files?: Array<{
      type: string
      uri: string
    }>
    category?: string
  }
}

export interface TotemAttribute {
  trait_type: string
  value: string | number
  display_type?: string
}

export interface TotemMetadataInput {
  collections: TotemCollection[]
  walletAddress: string
  ensName?: string | null
  tokenId: number
  imageUrl: string
  externalUrl?: string
}

/**
 * Generate NFT metadata for a totem (simplified version for API)
 * @param tokenId - Token ID
 * @param collections - Array of collection IDs or detailed collection objects
 * @returns Generated metadata object
 */
export async function generateTotemMetadataForAPI(tokenId: number, collections: string[] | CollectionSnapshot[]): Promise<TotemMetadata> {
  const baseUrl = getBaseUrl()

  // Generate name with token ID
  const name = `#${tokenId}`

  // Description (avoid dynamic timestamps here; cache-busting handled via image/external_url params)
  const description = `NFTotem transforms the collections in your Ethereum wallet into a living, stackable totem. Each block represents your NFTs. The more you hold, the taller and rarer your totem becomes.`

  // Exclude base-cube from collections for traits
  const userCollections = collections.filter(c => {
    const id = typeof c === 'string' ? c : c.id
    return id !== "base-cube"
  })

  // Generate attributes
  const attributes: TotemAttribute[] = []

  // Add Blocks attribute (without base-cube)
  attributes.push({
    trait_type: "Blocks",
    value: userCollections.length
  })

  // Add Collection attributes with new trait system
  // Each collection becomes its own trait_type to avoid OpenSea merging
  // Use a Set to track unique collection names and avoid duplicates
  const seenCollectionNames = new Set<string>()
  
  userCollections.forEach((collection, index) => {
    let collectionName: string
    let variant: string
    
    if (typeof collection === 'string') {
      // Old format - collection ID string
      const collectionObj = COLLECTIONS.find(col => col.id === collection)
      collectionName = collectionObj?.name || collection
      variant = "Default" // Old format always uses default
    } else {
      // New format - detailed collection object
      // Always try to get display name from slug mapping first
      const displayName = getDisplayNameFromSlug(collection.name)
      
      if (displayName !== collection.name) {
        // Found a mapping, use the display name
        collectionName = displayName
        console.log(`[METADATA] Converted "${collection.name}" to display name "${collectionName}"`)
      } else {
        // No mapping found, use as-is
        collectionName = collection.name
      }
      
      variant = getBlockVariant(collection.cubeImage)
    }
    
    // Normalize collection name to avoid duplicates
    // Convert to lowercase and remove special characters for comparison
    const normalizedName = collectionName.toLowerCase().replace(/[^a-z0-9]/g, '')
    
    // Only add if we haven't seen this collection name before
    if (!seenCollectionNames.has(normalizedName)) {
      seenCollectionNames.add(normalizedName)
      attributes.push({
        trait_type: collectionName,
        value: variant
      })
    } else {
      console.warn(`[METADATA] Skipping duplicate collection: ${collectionName} (normalized: ${normalizedName})`)
    }
  })

  // Add token ID (optional, if needed)
  // attributes.push({
  //   trait_type: "Token ID",
  //   value: tokenId,
  //   display_type: "number"
  // })

  return {
    name,
    description,
    image: `${baseUrl}/api/image/${tokenId}?v=${Date.now()}`,
    external_url: `${baseUrl}/totem/${tokenId}?v=${Date.now()}`,
    attributes
  }
}

/**
 * Generate NFT metadata for a totem
 * Uses OpenRarity mechanics - no custom rarity calculation
 * @param input - Input data for metadata generation
 * @returns Generated metadata object
 */
export function generateTotemMetadata(input: TotemMetadataInput): TotemMetadata {
  const { collections, walletAddress, ensName, tokenId, imageUrl, externalUrl } = input

  // Generate name with token ID
  const name = `#${tokenId}`

  // New description from user
  const description = `NFTotem transforms the collections in your Ethereum wallet into a living, stackable totem. Each block represents your NFTs. The more you hold, the taller and rarer your totem becomes.`

  // Generate attributes
  const attributes: TotemAttribute[] = []

  // Add Block Count attribute
  attributes.push({
    trait_type: "Blocks",
    value: collections.length
  })

  // Add Collection attributes (one for each collection)
  collections.forEach((collection, index) => {
    attributes.push({
      trait_type: `Collection #${index + 1}`,
      value: collection.name
    })
  })

  // Add token ID
  attributes.push({
    trait_type: "Token ID",
    value: tokenId,
    display_type: "number"
  })

  return {
    name,
    description,
    image: imageUrl,
    external_url: `${externalUrl}?v=${Date.now()}`,
    attributes
  }
}

/**
 * Validate generated metadata
 * @param metadata - Metadata to validate
 * @returns Validation result
 */
export function validateTotemMetadata(metadata: TotemMetadata): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check required fields
  if (!metadata.name) {
    errors.push("Name is required")
  }

  if (!metadata.description) {
    errors.push("Description is required")
  }

  if (!metadata.image) {
    errors.push("Image URL is required")
  }

  if (!metadata.attributes || !Array.isArray(metadata.attributes)) {
    errors.push("Attributes array is required")
  } else {
    // Validate attributes
    metadata.attributes.forEach((attr, index) => {
      if (!attr.trait_type) {
        errors.push(`Attribute ${index}: trait_type is required`)
      }
      if (attr.value === undefined || attr.value === null) {
        errors.push(`Attribute ${index}: value is required`)
      }
    })
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Get collection statistics for OpenRarity integration
 * This can be used by external OpenRarity services
 * @param collections - Array of collections
 * @returns Collection statistics
 */
export function getCollectionStatistics(collections: TotemCollection[]) {
  const collectionCounts: Record<string, number> = {}
  
  collections.forEach(collection => {
    collectionCounts[collection.name] = (collectionCounts[collection.name] || 0) + 1
  })

  return {
    totalCollections: collections.length,
    uniqueCollections: Object.keys(collectionCounts).length,
    collectionBreakdown: collectionCounts
  }
}

/**
 * Format metadata for OpenSea compatibility
 * @param metadata - Raw metadata
 * @returns OpenSea-compatible metadata
 */
export function formatForOpenSea(metadata: TotemMetadata): TotemMetadata {
  return {
    ...metadata,
    // Ensure proper formatting for OpenSea
    attributes: metadata.attributes.map(attr => ({
      ...attr,
      trait_type: attr.trait_type,
      value: attr.value,
      display_type: attr.display_type
    }))
  }
}

/**
 * Convert metadata to JSON string
 * @param metadata - Metadata object
 * @returns JSON string
 */
export function metadataToJson(metadata: TotemMetadata): string {
  return JSON.stringify(metadata, null, 2)
}

/**
 * Parse metadata from JSON string
 * @param json - JSON string
 * @returns Metadata object
 */
export function metadataFromJson(json: string): TotemMetadata {
  return JSON.parse(json) as TotemMetadata
} 